import clsx from "clsx";
import {
  MouseEventHandler,
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useEvent from "react-use-event-hook";

import { PIC, SVG_MUI } from "@ryde/assets";
import { useDynamicFormsUtils, useTableFiltersActions } from "@ryde/redux";
import { ICustomField } from "@ryde/shared";

import {
  NumberFieldWrapper,
  TextFieldInputStyled,
  TextFieldLabel,
} from "./number.styled";

interface IFilterNumberBetween {
  customFieldId: number;
  control: ICustomField;
  value: [number, number] | null;
}

export const FilterNumberBetween = ({
  customFieldId,
  control,
  value,
}: IFilterNumberBetween): ReactElement => {
  const numberBetweenParams = control.filterAttributes?.numberBetween;
  const [minValue, setMinValue] = useState<string | undefined>("");
  const [maxValue, setMaxValue] = useState<string | undefined>("");
  const isFirstRender = useRef(true);

  const { getGridControlLabelById } = useDynamicFormsUtils();
  const { setValuesToFilter } = useTableFiltersActions();
  const { handleRemoveFilter } = useTableFiltersActions();
  const label = customFieldId ? getGridControlLabelById(customFieldId) : "";

  const removeFilter: MouseEventHandler = e => {
    e.stopPropagation();
    handleRemoveFilter(customFieldId);
  };

  const updateFilterValue = useEvent(
    (newMinValueStr?: string, newMaxValueStr?: string): void => {
      const newMinValue = Number(newMinValueStr);
      const newMaxValue = Number(newMaxValueStr);

      const nextFilterValue = [];

      if (
        newMinValueStr &&
        typeof newMinValue === "number" &&
        !isNaN(newMinValue)
      ) {
        nextFilterValue.push(newMinValue);
      } else {
        nextFilterValue.push(0);
      }

      if (
        newMaxValueStr &&
        typeof newMaxValue === "number" &&
        !isNaN(newMaxValue)
      ) {
        nextFilterValue.push(newMaxValue);
      }

      setValuesToFilter(customFieldId, nextFilterValue);
    },
  );

  const handleMinChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const newValue = event.target.value.replace(/[^0-9.]/g, "");

    setMinValue(newValue);
    updateFilterValue(newValue, maxValue);
  };

  const handleMaxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const newValue = event.target.value.replace(/[^0-9.]/g, "");

    setMaxValue(newValue);
    updateFilterValue(minValue, newValue);
  };

  const isError = useMemo(() => {
    if (Array.isArray(value)) {
      if (value.length < 2) {
        return true;
      }

      if (value[1] < value[0]) {
        return true;
      }
    }

    return false;
  }, [value]);

  useEffect(() => {
    if (isFirstRender.current) {
      const defaultMin = numberBetweenParams?.defaultMin
        ? String(numberBetweenParams.defaultMin)
        : "";
      const defaultMax = numberBetweenParams?.defaultMax
        ? String(numberBetweenParams.defaultMax)
        : "";

      if (defaultMin || defaultMax) {
        updateFilterValue(defaultMin, defaultMax);
      }

      isFirstRender.current = false;
      return;
    }

    if (!value) {
      setMinValue("");
      setMaxValue("");
      return;
    }

    if (Array.isArray(value)) {
      const [min, max] = value;

      if (min) {
        setMinValue(String(min));
      }

      if (max) {
        setMaxValue(String(max));
      }
    }
  }, [
    numberBetweenParams?.defaultMax,
    numberBetweenParams?.defaultMin,
    updateFilterValue,
    value,
  ]);

  return (
    <NumberFieldWrapper
      className={clsx({
        field__error: isError,
      })}
    >
      <TextFieldLabel htmlFor={label}>{label}</TextFieldLabel>
      <TextFieldInputStyled
        name={"min"}
        placeholder="--"
        value={minValue}
        onChange={handleMinChange}
      />

      <span>
        {document?.documentElement?.dir === "rtl" ? (
          <PIC.ArrowFullLeft />
        ) : (
          <PIC.ArrowFullRight />
        )}
      </span>

      <TextFieldInputStyled
        name={"max"}
        placeholder="--"
        value={maxValue}
        onChange={handleMaxChange}
      />

      <SVG_MUI.CircleXMark
        className="circle-x-mark"
        onClick={e => removeFilter(e)}
      />
    </NumberFieldWrapper>
  );
};
